import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Eye, PenLine, Zap } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import type { DepartmentStatus } from '@/types/api';
import { cn } from '@/lib/utils';

const MODE_CONFIG: Record<
  DepartmentStatus['agentMode'],
  { label: string; icon: typeof Eye; className: string; description: string }
> = {
  monitor: {
    label: 'Monitor',
    icon: Eye,
    className: 'text-blue-400',
    description: 'Agent detects changes and creates review cards but does not draft revisions.',
  },
  draft: {
    label: 'Draft',
    icon: PenLine,
    className: 'text-amber-400',
    description: 'Agent drafts proposed revisions for reviewer approval.',
  },
  proactive: {
    label: 'Proactive',
    icon: Zap,
    className: 'text-green-400',
    description: 'Agent drafts and auto-publishes low-risk changes, escalates high-risk ones.',
  },
};

function formatRelativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffHr / 24);
  if (diffHr < 1) return 'just now';
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${diffDays}d ago`;
}

export function DepartmentsTab() {
  const queryClient = useQueryClient();

  const { data: departments, isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.departments.list(),
    staleTime: 60_000,
  });

  const modeMutation = useMutation({
    mutationFn: ({ department, mode }: { department: string; mode: string }) =>
      api.departments.updateMode(department, mode),
    onMutate: async ({ department, mode }) => {
      await queryClient.cancelQueries({ queryKey: ['departments'] });
      const previous = queryClient.getQueryData<DepartmentStatus[]>(['departments']);
      queryClient.setQueryData<DepartmentStatus[]>(['departments'], (old) =>
        old?.map((d) =>
          d.department === department
            ? { ...d, agentMode: mode as DepartmentStatus['agentMode'] }
            : d,
        ),
      );
      return { previous };
    },
    onSuccess: (_, { department, mode }) => {
      toast.success(`${department} switched to ${mode} mode.`);
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['departments'], context.previous);
      }
      toast.error('Failed to update department mode.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4 pt-6">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (!departments || departments.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <p className="text-sm text-muted-foreground">
          No departments configured for this tenant.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          {departments.length} Department{departments.length === 1 ? '' : 's'}
        </h3>
      </div>

      <div className="space-y-3">
        {departments.map((dept) => {
          const modeCfg = MODE_CONFIG[dept.agentMode];
          const ModeIcon = modeCfg.icon;

          return (
            <Card key={dept.department}>
              <CardContent className="flex items-center gap-4 p-5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    {dept.displayName}
                  </p>
                  <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{dept.pendingReviews} pending reviews</span>
                    <span>{dept.queuedPublishes} queued publishes</span>
                    {dept.lastActivityAt && (
                      <span>Last active: {formatRelativeTime(dept.lastActivityAt)}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <ModeIcon className={cn('h-4 w-4', modeCfg.className)} />
                    <span className={cn('text-xs font-medium', modeCfg.className)}>
                      {modeCfg.label}
                    </span>
                  </div>

                  <Select
                    value={dept.agentMode}
                    onValueChange={(val) =>
                      modeMutation.mutate({ department: dept.department, mode: val as string })
                    }
                  >
                    <SelectTrigger size="sm" className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monitor">
                        <Eye className="mr-1.5 h-3.5 w-3.5 text-blue-400" />
                        Monitor
                      </SelectItem>
                      <SelectItem value="draft">
                        <PenLine className="mr-1.5 h-3.5 w-3.5 text-amber-400" />
                        Draft
                      </SelectItem>
                      <SelectItem value="proactive">
                        <Zap className="mr-1.5 h-3.5 w-3.5 text-green-400" />
                        Proactive
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Mode Descriptions
        </h4>
        <div className="space-y-2">
          {(Object.entries(MODE_CONFIG) as [DepartmentStatus['agentMode'], typeof MODE_CONFIG['monitor']][]).map(
            ([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <div key={key} className="flex items-start gap-2">
                  <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', cfg.className)} />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{cfg.label}:</span>{' '}
                    {cfg.description}
                  </p>
                </div>
              );
            },
          )}
        </div>
      </div>
    </div>
  );
}
